import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import { Box, Button, Container, IconButton, Stack, Typography } from '@mui/material';
import { isTauri } from '@tauri-apps/api/core';
import { useAuthStore } from 'cloak-auth/src/Client/stores';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { portalMenuItems } from '@ui/Components/Layout/AppBar/AppBarProfileMenu/MenuItems/portalMenuItems';
import NavigationLink from '@ui/Components/Link/NavigationLink';
import SupportTicketModal from '@ui/Components/Modals/SupportTicketDialog';
import { useHasNext } from '@ui/Hooks/useHasNext';
import useTermsOfServiceUrl from '@ui/Hooks/useTermsOfServiceUrl';
import getCDNImagePath from '@ui/Utils/getCDNImagePath';

import { ProfileMenuContent } from 'kerberus-components/src/Components/Layout/AppBar/AppBarProfileMenu/ProfileMenuContent';
import MobileDrawer from 'kerberus-components/src/Components/Layout/MobileDrawer';

import { NavItems } from './AppData';
import DownloadAppsModal from './DownloadAppsModal';
import SessionInfo from './SessionInfo';

interface TopNavigationBarProps {
    readonly navBarHeight: string;
}

export default function TopNavigationBar(props: TopNavigationBarProps) {
    const { name: fullName } = useAuthStore.getState().decodedToken!;

    const hasNext = useHasNext();
    const { navBarHeight } = props;
    const navigate = useNavigate();
    const location = useLocation();
    const termsUrl = useTermsOfServiceUrl();

    const tauri = isTauri();

    const [appsModal, setAppsModal] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
    const [contactSupportOpen, setContactSupportOpen] = useState(false);

    const isRootRoute = (): boolean => {
        const pathSegments = location.pathname.split('/').filter(Boolean);
        return pathSegments.length <= 1;
    };

    const getPageTitle = (): string => {
        const pathSegments = location.pathname.split('/').filter(Boolean);

        const mainRoute = pathSegments[0];

        switch (mainRoute) {
            case 'device':
                return pathSegments.length > 1 ? 'Device Details' : 'Devices';
            case '':
                return 'Places';
            case 'map':
                return 'Places Map';
            case 'tagclaim':
                return 'Claim Tag';
            case 'tagredirect':
                return 'Redirect Tag';
            case 'testrenkitag':
                return 'Test Renki Tag';
            case 'settings':
                return 'Settings';
            case 'scan':
                return 'Scan Tag';
            default:
                return 'Places';
        }
    };

    const handleBackClick = () => {
        navigate(-1);
    };

    // Check if first-time launch to show apps modal
    useEffect(() => {
        if (!tauri) {
            const hasBeenRead = localStorage.getItem('appModalRead');

            if (!hasBeenRead) {
                setAppsModal(true);
            }
        }
    }, [tauri]);

    const handleAppModalClose = () => {
        localStorage.setItem('appModalRead', 'true');
        setAppsModal(false);
    };

    const handleContactSupportOpen = () => {
        setContactSupportOpen(true);
        setDrawerOpen(false);
    };

    const handleContactSupportClose = () => {
        setContactSupportOpen(false);
    };

    const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const toggleDrawer = (open: boolean) => () => {
        setDrawerOpen(open);
    };

    const handleAppsClick = () => {
        setAppsModal(true);
        setDrawerOpen(false);
    };

    // Get profile menu items for both desktop and mobile
    const menuItems = portalMenuItems({
        handleCloseUserMenu,
        handleContactSupportOpen,
        hasNext: hasNext ?? false,
        termsUrl,
    });

    return (
        <Box
            sx={{
                backgroundColor: '#edf2f6',
                borderBottom: '2px solid',
                borderColor: 'divider',
                display: 'flex',
                height: navBarHeight,
            }}
        >
            <Container
                disableGutters
                maxWidth="lg"
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flex: 1,
                    justifyContent: 'space-between',
                    p: { lg: 3, xs: 1 },
                    position: 'relative',
                }}
            >
                {/* Left side - Logo (Desktop) / Back button (Mobile when not on root) */}
                <Box sx={{ alignItems: 'center', display: 'flex', zIndex: 2 }}>
                    {!isRootRoute() && (
                        <IconButton
                            onClick={handleBackClick}
                            sx={{
                                display: { lg: 'none', xs: 'flex' },
                                mr: 1,
                            }}
                        >
                            <ArrowBackIcon sx={{ color: 'primary.main' }} />
                        </IconButton>
                    )}

                    <NavigationLink to="/">
                        <Box
                            sx={{
                                alignItems: 'center',
                                display: { lg: 'flex', xs: 'none' },
                                fontSize: '1.25rem',
                                marginRight: '1rem',
                                paddingBottom: '0.3125rem',
                                paddingTop: '0.3125rem',
                                textDecoration: 'none',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            <img alt="Renki Logo" height={50} src={getCDNImagePath('renki/renkilogo_text.png')} />
                        </Box>
                    </NavigationLink>
                    {isRootRoute() && (
                        <NavigationLink to="/">
                            <Box
                                sx={{
                                    alignItems: 'center',
                                    display: { lg: 'none', xs: 'flex' },
                                    justifyContent: 'center',
                                    ml: 1,
                                }}
                            >
                                <img alt="Renki Logo" height={30} src={getCDNImagePath('renki/renkilogo.png')} />
                            </Box>
                        </NavigationLink>
                    )}
                </Box>

                {/* Center - Page Title for mobile */}
                <Typography
                    component="h1"
                    sx={{
                        color: 'primary.main',
                        display: { lg: 'none', xs: 'block' },
                        fontSize: '1.1rem',
                        fontWeight: 500,
                        left: '50%',
                        letterSpacing: 0.3,
                        position: 'absolute',
                        transform: 'translateX(-50%)',
                    }}
                    variant="h6"
                >
                    {getPageTitle()}
                </Typography>

                {/* Desktop Navigation */}
                <Box sx={{ display: { lg: 'flex', xs: 'none' } }}>
                    <Stack
                        direction="row"
                        spacing={2}
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        {!tauri && (
                            <Button
                                onClick={() => {
                                    setAppsModal(true);
                                }}
                                sx={{
                                    color: 'mlBlue.main',
                                    fontSize: 15,
                                    padding: '0.5rem 1rem',
                                    textTransform: 'none',
                                }}
                                variant="text"
                            >
                                Apps
                            </Button>
                        )}

                        {NavItems.map((item) => (
                            <Button
                                key={item.label}
                                onClick={() => {
                                    navigate(item.to);
                                }}
                                sx={{
                                    color: 'mlBlue.main',
                                    fontSize: 15,
                                    padding: '0.5rem 1rem',
                                    textTransform: 'none',
                                }}
                                variant="text"
                            >
                                {item.label}
                            </Button>
                        ))}

                        {/* Desktop Profile Button */}
                        <IconButton onClick={handleOpenUserMenu} size="large">
                            <PersonIcon sx={{ color: 'primary.main', height: 34, width: 34 }} />
                        </IconButton>

                        <ProfileMenuContent
                            anchorElUser={anchorElUser}
                            fullName={fullName}
                            handleCloseUserMenu={handleCloseUserMenu}
                            hasNext={hasNext}
                            menuItems={menuItems}
                        />
                    </Stack>
                </Box>

                {/* Mobile Menu Button */}
                <Box sx={{ display: { lg: 'none', xs: 'block' }, zIndex: 2 }}>
                    <IconButton aria-label="open menu" onClick={toggleDrawer(true)} size="medium">
                        <MenuIcon sx={{ color: 'primary.main' }} />
                    </IconButton>
                </Box>

                {/* Mobile-only Drawer with Profile Menu */}
                <MobileDrawer
                    fullName={fullName}
                    menuItems={menuItems}
                    onAppsClick={handleAppsClick}
                    onClose={toggleDrawer(false)}
                    onOpen={toggleDrawer(true)}
                    open={drawerOpen}
                    showAppsButton={!tauri}
                />

                {/* Apps Modal */}
                <DownloadAppsModal onClose={handleAppModalClose} open={appsModal} />

                <SupportTicketModal addressfront onClose={handleContactSupportClose} open={contactSupportOpen} />
            </Container>
            {hasNext ? <SessionInfo /> : null}
        </Box>
    );
}
